/**
 * TradeRow - Insider trade display row
 */
import type { InsiderTrade, InsiderTradeListItem } from '../../types/research'

interface TradeRowProps {
  trade: InsiderTrade | InsiderTradeListItem
}

// Type guard to check if trade is InsiderTradeListItem
function isTradeListItem(
  trade: InsiderTrade | InsiderTradeListItem
): trade is InsiderTradeListItem {
  return 'is_buy' in trade
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatShares(shares: number): string {
  const absShares = Math.abs(shares)
  if (absShares >= 1_000_000) {
    return `${(absShares / 1_000_000).toFixed(2)}M`
  }
  if (absShares >= 1_000) {
    return `${(absShares / 1_000).toFixed(1)}K`
  }
  return absShares.toLocaleString()
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '--'
  }
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000) {
    return `$${(absValue / 1_000_000).toFixed(2)}M`
  }
  if (absValue >= 1_000) {
    return `$${(absValue / 1_000).toFixed(1)}K`
  }
  return `$${absValue.toLocaleString()}`
}

function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '--'
  }
  return `$${value.toFixed(2)}`
}

function getTradeType(trade: InsiderTrade | InsiderTradeListItem): {
  isBuy: boolean
  label: string
} {
  if (isTradeListItem(trade)) {
    return {
      isBuy: trade.is_buy,
      label: trade.is_buy ? 'Buy' : 'Sell',
    }
  }

  // For full InsiderTrade type
  const type = trade.transaction_type
  if (type === 'buy') {
    return { isBuy: true, label: 'Buy' }
  }
  if (type === 'sell') {
    return { isBuy: false, label: 'Sell' }
  }
  if (type === 'exercise') {
    return { isBuy: true, label: 'Exercise' }
  }
  if (type === 'gift') {
    return { isBuy: false, label: 'Gift' }
  }
  // Determine by shares sign
  return {
    isBuy: trade.shares > 0,
    label: trade.shares > 0 ? 'Buy' : 'Sell',
  }
}

export function TradeRow({ trade }: TradeRowProps) {
  const { isBuy, label: tradeTypeLabel } = getTradeType(trade)

  const transactionValue =
    'transaction_value' in trade ? trade.transaction_value : null
  const pricePerShare = trade.price_per_share

  return (
    <div
      className={`p-4 rounded-lg border ${
        isBuy
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Insider info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 truncate">
              {trade.insider_name}
            </p>
            {/* Trade type badge */}
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                isBuy
                  ? 'bg-green-200 text-green-800'
                  : 'bg-red-200 text-red-800'
              }`}
            >
              {tradeTypeLabel}
            </span>
          </div>
          {trade.insider_title && (
            <p className="text-sm text-gray-600 truncate mt-0.5">
              {trade.insider_title}
            </p>
          )}
        </div>

        {/* Trade details */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {/* Date */}
          <div className="text-gray-600">
            <span className="text-gray-500">Date:</span>{' '}
            <span className="font-medium text-gray-900">
              {formatDate(trade.transaction_date)}
            </span>
          </div>

          {/* Shares */}
          <div className="text-gray-600">
            <span className="text-gray-500">Shares:</span>{' '}
            <span
              className={`font-medium ${
                isBuy ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {isBuy ? '+' : '-'}
              {formatShares(trade.shares)}
            </span>
          </div>

          {/* Price per share */}
          {pricePerShare !== null && pricePerShare !== undefined && (
            <div className="text-gray-600">
              <span className="text-gray-500">Price:</span>{' '}
              <span className="font-medium text-gray-900">
                {formatPrice(pricePerShare)}
              </span>
            </div>
          )}

          {/* Total value */}
          {transactionValue !== null && transactionValue !== undefined && (
            <div className="text-gray-600">
              <span className="text-gray-500">Value:</span>{' '}
              <span
                className={`font-medium ${
                  isBuy ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {formatCurrency(transactionValue)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TradeRow
